'use client';

import { useHandleIFrameMessages } from '@/hooks/useHandleIFrameMessages';
import useJWT from '@/hooks/useJWT';
import type { Replica as ReplicaType } from '@/models/replicas';
import type { Tables } from '@/models/supabase.types';
import { useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';
import ChatLoadingSkeleton from './chat-loading-skeleton';
const Chat = dynamic(() => import('./chat'), {
  ssr: false,
  loading: () => <ChatLoadingSkeleton />
});
interface ReplicaProps {
  replica: ReplicaType & {
    chat_widget_settings: Tables<'chat_widget_settings'> | null;
  };
}
export default function Replica({
  replica
}: ReplicaProps) {
  const {
    jwt,
    register
  } = useJWT();
  const searchParams = useSearchParams();
  const [suggestedQuestion, setSuggestedQuestion] = useState<string | null>(null);
  const {
    config
  } = useHandleIFrameMessages({
    onSuggestedQuestion: question => {
      setSuggestedQuestion(question);
    }
  });
  const shouldLoadHistory = !!jwt;
  const {
    data: chatHistory,
    status
  } = useQuery({
    queryKey: ['chat-history', replica.uuid],
    queryFn: async () => {
      const response = await fetch(`/api/chat/${replica.uuid}/history`, {
        method: 'POST',
        body: JSON.stringify({
          jwt
        })
      });
      if (!response.ok) {
        if (response.status === 401) {
          // renew jwt and retry
          await register();
        }
        throw new Error('Failed to fetch chat history');
      }
      const data = await response.json();
      return data.items;
    },
    enabled: shouldLoadHistory,
    staleTime: 0
  });
  if (status === 'pending' && !chatHistory && shouldLoadHistory) {
    return <ChatLoadingSkeleton />;
  }
  const isPreview = searchParams.get('isPreview') === 'true';
  const hideBranding = isPreview && config ? config.hideBranding : replica.chat_widget_settings?.hide_branding;
  return <Chat replica={replica} classNameInput="text-foreground" history={chatHistory} key={replica.uuid} suggestedQuestion={suggestedQuestion} hideBranding={hideBranding} data-sentry-element="Chat" data-sentry-component="Replica" data-sentry-source-file="replica.tsx" />;
}